import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditEncounterModal from "./EditEncounterModal";
import DateRangeFilter from "../../../components/DateRangeFilter";
import ListPageLayout from "../../../components/ListPageLayout";
import {
  fetchEncounterWorklist,
  fetchStudies,
} from "../../../utils/api";
import { useAuth } from "../../../components/AuthProvider";
import { getStatusChipStyle, BASE_CHIP_SX } from "../../../constants/statusColors";
import {
  FAR_FUTURE_DATE_FILTER_END,
  getChicagoDateKeyWithOffset,
  getDefaultDateFiltersTodayToNextMonth,
} from "../../../utils/dateFilters";
import { formatDateTimeInAppTimeZone, formatDateInAppTimeZone } from "../../../utils/appDateTime";
import { BIOBANK_ROUTES, BIOBANK_SUB_ROUTES } from "../../../constants/routes";

const TZ = "America/Chicago";

const getDefaultScheduleDateFilters = () => getDefaultDateFiltersTodayToNextMonth();

const readPath = (source, path) =>
  path.split(".").reduce((value, key) => value?.[key], source);

const firstValue = (source, paths, fallback = "") => {
  for (const path of paths) {
    const value = readPath(source, path);
    if (value !== null && typeof value !== "undefined" && value !== "") {
      return value;
    }
  }
  return fallback;
};

const formatDateTime = (value) => {
  if (!value) return "N/A";
  return formatDateTimeInAppTimeZone(value, String(value));
};

const formatDateOnly = (value) => {
  if (!value) return "N/A";
  return formatDateInAppTimeZone(value, String(value));
};

const formatAddress = (row, multiline = false) => {
  const patient = row.patient || row.participant || {};
  const line1 = firstValue(row, [
    "mailingAddressLine1",
    "homeAddressLine1",
    "patient.homeAddressLine1",
    "participant.homeAddressLine1",
  ]);
  const line2 = firstValue(row, [
    "mailingAddressLine2",
    "homeAddressLine2",
    "patient.homeAddressLine2",
    "participant.homeAddressLine2",
  ]);
  const city = firstValue(row, ["city", "patient.city", "participant.city"]);
  const state = firstValue(row, ["state", "patient.state", "participant.state"]);
  const zip = firstValue(row, [
    "zipCode",
    "zip",
    "patient.zipCode",
    "patient.zip",
    "participant.zipCode",
    "participant.zip",
  ]);

  const locality = [city, state, zip].filter(Boolean).join(", ");
  const parts = [line1, line2, locality].filter(Boolean);
  if (parts.length === 0 && patient.homeAddress) {
    return patient.homeAddress;
  }
  if (parts.length === 0) return "N/A";
  return multiline ? parts.join("\n") : parts.join(", ");
};

const PatientSchedule = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const [matchedEhr, setMatchedEhr] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [studies, setStudies] = useState([]);
  const [totalRowCount, setTotalRowCount] = useState(0);
  
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [encounterToEdit, setEncounterToEdit] = useState(null);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const defaultDateFilters = useMemo(getDefaultScheduleDateFilters, []);
  const [dateFilters, setDateFilters] = useState(defaultDateFilters);

  useEffect(() => {
    let popupTimer;
    if (location.state?.matchedEhr) setMatchedEhr(location.state.matchedEhr);
    if (location.state?.showToast) {
      setShowToast(true);
      popupTimer = setTimeout(() => {
        setShowToast(false);
        navigate(location.pathname, { replace: true, state: {} });
      }, 5000);
    }

    return () => {
      if (popupTimer) clearTimeout(popupTimer);
    };
  }, [location.state, location.pathname, navigate]);

  const loadScheduledPatients = useCallback(async () => {
    const getStudyNames = (studyIds, allStudies) =>
      studyIds.map((studyId) => {
        const study = allStudies.find((s) => s.irbStudy === studyId);
        return study
          ? `${study.irbStudy}: ${study.studyShortName || study.studyName || study.irbStudy}`
          : studyId;
      });

    setLoading(true);
    try {
      let studiesData = studies;
      if (!studiesData || studiesData.length === 0) {
        studiesData = await fetchStudies();
        setStudies(studiesData || []);
      }

      const response = await fetchEncounterWorklist(
        paginationModel.page,
        paginationModel.pageSize,
        dateFilters,
      );
      const encountersData = Array.isArray(response?.content)
        ? response.content
        : [];

      const transformed = encountersData
        .map((item, index) => {
          if (!item || !item.encounterId) return null;

          const encounterDate = item.encounterDateTime
            ? new Date(item.encounterDateTime)
            : null;

          const studyIds = item.scheduledStudyIds || [];
          const studyNames = getStudyNames(studyIds, studiesData || []);

          const fallbackId = [
            item.patientAccountNumber || "NA",
            item.encounterDateTime || index,
          ].join("_");

          return {
            id: item.encounterId || fallbackId,
            patientId: item.patientId,
            name: item.patientName || "N/A",
            accountNumber: item.patientAccountNumber || "N/A",
            appointmentDateTime: formatDateTime(item.encounterDateTime),
            encounterDateTime: item.encounterDateTime,
            appointmentDateObj: encounterDate,
            studyNames,
            consentFormStatus: item.consentStatus || "Incomplete",
            checkInStatus: item.checkInStatus,
          };
        })
        .filter((row) => row !== null)
        .sort((a, b) => b.appointmentDateObj - a.appointmentDateObj);

      setRows(transformed);
      setTotalRowCount(response?.totalElements || 0);
    } catch (err) {
      console.error(err);
      setRows([]);
      setTotalRowCount(0);
    } finally {
      setLoading(false);
    }
  }, [dateFilters, paginationModel.page, paginationModel.pageSize, studies]);

  const applyDateFilters = useCallback((nextFilters) => {
    setDateFilters({
      encounterDateFrom: nextFilters.encounterDateFrom || "",
      encounterDateTo: nextFilters.encounterDateTo || "",
    });
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  const resetDateFilters = useCallback(() => {
    setDateFilters(defaultDateFilters);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [defaultDateFilters]);

  useEffect(() => {
    loadScheduledPatients();
  }, [loadScheduledPatients]);

  const scheduledColumns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Participant Name",
        flex: 1,
        headerAlign: "center",
        align: "center",
      },
      {
        field: "accountNumber",
        headerName: "MRN",
        flex: 0.8,
        headerAlign: "center",
        align: "center",
      },
      {
        field: "appointmentDateTime",
        headerName: "Appointment Time",
        flex: 1,
        headerAlign: "center",
        align: "center",
      },
      {
        field: "studyNames",
        headerName: "Scheduled Studies",
        flex: 2,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => {
          const studyNames = params.value || [];
          if (studyNames.length === 0) {
            return (
              <Typography color="text.secondary">
                No studies assigned
              </Typography>
            );
          }
          return (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: "1px",
                alignItems: "center",
                justifyContent: "center",
                alignContent: "center",
                py: 1,
                width: "100%",
                height: "100%",
              }}
            >
              {studyNames.map((study, idx) => (
                <Chip
                  key={`${params.row.id}-${study}-${idx}`}
                  label={study}
                  size="small"
                  variant="outlined"
                  clickable={false}
            onClick={(e) => e.stopPropagation()}
                  sx={{
                    fontSize: "0.75rem",
                    maxWidth: "200px",
                    cursor: "default",
                    "& .MuiChip-label": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                  }}
                />
              ))}
            </Box>
          );
        },
      },
      {
        field: "consentFormStatus",
        headerName: "Consent Status",
        flex: 1,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => {
          const status = params.row.consentFormStatus || params.value;
          return (
            <Chip
              label={status || "Unknown"}
              size="small"
              sx={{
                ...BASE_CHIP_SX,
                ...getStatusChipStyle(status || "Unknown"),
              }}
            />
          );
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        flex: 1,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => {
          if (params.row.checkInStatus === "CHECKED_IN") {
            return (
              <Button variant="outlined" sx={{ width: "120px", height: "32px", fontSize: "13px", fontWeight: 600, borderRadius: "10px", padding: 0, textTransform: "none" }} disabled>
                Checked In
              </Button>
            );
          }

          return (
            <Button
              variant="contained"
              onClick={(e) => {
                e.stopPropagation();
                setEncounterToEdit(params.row);
                setEditModalOpen(true);
              }}
              sx={{
                width: "120px",
                height: "32px",
                fontSize: "13px",
                fontWeight: 600,
                borderRadius: "10px",
                textTransform: "none",
              }}
            >
              Reschedule
            </Button>
          );
        },
      }
    ],
    [],
  );

  const displayedRows = useMemo(
    () =>
      matchedEhr ? rows.filter((r) => r.accountNumber === matchedEhr) : rows,
    [rows, matchedEhr],
  );
  const gridRowCount = matchedEhr ? displayedRows.length : totalRowCount;

  const handleAddPatientClick = () => navigate(BIOBANK_SUB_ROUTES.PARTICIPANT_SCHEDULING_VERIFY, {
    state: {
      flow: "schedule",
      returnTo: BIOBANK_ROUTES.PARTICIPANT_SCHEDULING,
    },
  });

  return (
    <>
      {showToast && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            position: "fixed",
            top: 60,
            zIndex: 1,
            left: "50%",
            transform: "translateX(-50%)",
          }}
        >
          <Alert
            icon={<CheckCircleIcon fontSize="inherit" />}
            variant="filled"
            severity="success"
            sx={{ maxWidth: 360 }}
          >
            Participant Added to the Schedule!
          </Alert>
        </Box>
      )}

      <ListPageLayout
        title="Participant Schedule"
        headerActions={
          <Button
            variant="contained"
            onClick={handleAddPatientClick}
            sx={{ fontWeight: 600, height: "40px", px: 3, fontSize: "14px", whiteSpace: "nowrap" }}
            data-tour-id="tour-schedule-participant"
          >
            SCHEDULE PARTICIPANT
          </Button>
        }
        filters={
          <Box data-tour-id="tour-schedule-date-filter">
            <DateRangeFilter
              value={dateFilters}
              defaultValue={defaultDateFilters}
              onSearch={applyDateFilters}
              onReset={resetDateFilters}
            />
          </Box>
        }
        gridTourId="tour-schedule-table"
        gridProps={{
          rows: displayedRows,
          columns: scheduledColumns,
          loading,
          pageSizeOptions: [10, 25, 50, 100],
          getRowId: (r) => r.id,
          getRowHeight: () => "auto",
          pagination: true,
          paginationMode: "server",
          rowCount: gridRowCount,
          paginationModel,
          onPaginationModelChange: setPaginationModel,
        }}
      />
      
      {encounterToEdit && (
        <EditEncounterModal
          open={editModalOpen}
          encounter={encounterToEdit}
          onClose={() => {
            setEditModalOpen(false);
            setEncounterToEdit(null);
          }}
          onSave={() => {
            setEditModalOpen(false);
            setEncounterToEdit(null);
            loadScheduledPatients();
          }}
        />
      )}
    </>
  );
};

export default PatientSchedule;